import { useState, useEffect, useCallback } from 'react';
import { SymptomEntry } from '../types';
import { StorageService } from '../services/storage';

export function useSymptomData() {
  const [entries, setEntries] = useState<SymptomEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await StorageService.getAllEntries();
      setEntries(data);
    } catch (error) {
      console.error('Failed to load entries:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const addEntry = async (entry: SymptomEntry) => {
    try {
      await StorageService.addEntry(entry);
      await loadEntries();
    } catch (error) {
      console.error('Failed to add entry:', error);
      throw error;
    }
  };

  const updateEntry = async (date: string, entry: SymptomEntry) => {
    try {
      await StorageService.updateEntry(date, entry);
      await loadEntries();
    } catch (error) {
      console.error('Failed to update entry:', error);
      throw error;
    }
  };

  const deleteEntry = async (date: string) => {
    try {
      await StorageService.deleteEntry(date);
      await loadEntries();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      throw error;
    }
  };

  const getEntryByDate = (date: string): SymptomEntry | null => {
    return entries.find(e => e.date === date) || null;
  };

  const clearAllEntries = async () => {
    try {
      await StorageService.clearEntries();
      await loadEntries();
    } catch (error) {
      console.error('Failed to clear entries:', error);
      throw error;
    }
  };

  return {
    entries,
    isLoading,
    addEntry,
    updateEntry,
    deleteEntry,
    getEntryByDate,
    clearAllEntries,
    refresh: loadEntries
  };
}
